import React, { useEffect, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { requiresRequestFlow, toLuxon } from 'tdr-common';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTableWithPolicies } from '../../../api/getTable';
import { useReleaseCheckoutSession } from '../../../api/releaseCheckoutSession';
import { analytics } from '../../../helpers/analytics';
import { CheckoutGuardOutletContext } from '../../../routes/CheckoutGuard';
import AgreementCheckbox from '../../agreement-checkbox/AgreementCheckbox';
import { CTAButton } from '../../buttons';
import { TermsAndConditions } from '../../dialog/info-dialog/TermsAndConditions';
import PolicySection from '../../policies/PolicySection';
import PolicyItems from '../PolicyItems';
import PolicyItemsLGB from '../PolicyItemsLGB';
import { CheckoutCTALabel, getCheckoutCTALabel } from '../../../context/new/CheckoutContext';
import { useCheckoutContext } from '../../../context/new/CheckoutContext';
import styles from './Policies.module.scss';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { resolveLocalizedText } from '@/helpers/resolveLocalizedText';
import { hasMinimumSpendPolicy } from '@/helpers/minimumSpendPolicy';

const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);

export const Policies = () => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { navConfig } = useCheckoutContext();

	const { invoice, checkoutSession, restaurant, featureFlags } = useOutletContext<CheckoutGuardOutletContext>();
	const { mutate: releaseCheckoutSession } = useReleaseCheckoutSession();
	const hasTrackedPoliciesView = useRef(false);

	const luxonStartDateTime = toLuxon(checkoutSession.time, restaurant.timezone);
	const {
		isCheckboxChecked,
		setIsCheckboxChecked,
		isMinimumSpendChecked,
		setIsMinimumSpendChecked,
		setAcceptedPolicyText
	} = useCheckoutContext();

	const { data: tableWithPolicies, isLoading, isError } = useTableWithPolicies({
		id: checkoutSession.tableId,
		searchParams: {
			groupSize: checkoutSession.guests,
			date: luxonStartDateTime.toFormat('yyyy-LL-dd'),
			time: luxonStartDateTime.toFormat('HH:mm')
		},
		location: 'checkout',
		// temp solution to get demo live
		venueId: restaurant.id
	});

	const requiresMinimumSpendConsent = hasMinimumSpendPolicy(tableWithPolicies?.policies);
	// An unresolved or failed policies query is indistinguishable from "no minimum spend
	// policy", so hold the CTA until the answer is known rather than letting a consent the
	// venue requires be skipped silently.
	const arePoliciesKnown = !isLoading && !isError;
	const isCtaEnabled =
		arePoliciesKnown && isCheckboxChecked && (!requiresMinimumSpendConsent || isMinimumSpendChecked);

	// Track Policies Viewed event once per page load
	useEffect(() => {
		if (!hasTrackedPoliciesView.current && tableWithPolicies && !isLoading) {
			const policies = tableWithPolicies?.policies || [];
			const policyTypes = policies.map(policy => policy.type);

			analytics.track('Policies Viewed', {
				restaurantId: restaurant.id,
				restaurantName: restaurant.name,
				tableId: tableWithPolicies.id,
				tableName: tableWithPolicies.name,
				checkoutId: checkoutSession.id,
				policyCount: policies.length,
				policyTypes,
				isLargeGroup: requiresRequestFlow(tableWithPolicies)
			});

			hasTrackedPoliciesView.current = true;
		}
	}, [tableWithPolicies, isLoading, restaurant, checkoutSession]);

	const isPaidBooking = invoice?.total > 0;
	const termsAndConditionsText = t('titles.terms_and_conditions');

	// Build plain text version for record-keeping
	let policyPlainText: string;
	const withPaymentCopy = (
		<>
			{t('checkout.checkbox.with_payment')} <TermsAndConditions />.
		</>
	);

	const noPaymentCopy = (
		<>
			{t('checkout.checkbox.without_payment')} <TermsAndConditions />.
		</>
	);

	let label = <>{isPaidBooking ? withPaymentCopy : noPaymentCopy}</>;

	if (restaurant.settings?.checkout?.termsLabel) {
		const customText = resolveLocalizedText(restaurant.settings?.checkout?.termsLabel, i18n.language);
		policyPlainText = `${customText} ${t('checkout.checkbox.custom_terms')} ${termsAndConditionsText}`;
		label = (
			<>
				<ReactMarkdown
					remarkPlugins={[remarkGfm]}
					components={{
						// Render inline without <p> wrapper
						p: ({ children }) => <>{children}</>,
						a: ({ href, children }) => (
							<a href={href} target="_blank" rel="noopener noreferrer" className="underline">
								{children}
							</a>
						)
					}}
				>
					{customText}
				</ReactMarkdown>
				{' '}{t('checkout.checkbox.custom_terms')} <TermsAndConditions />
			</>
		);
	}
	else {
		policyPlainText = isPaidBooking
			? `${t('checkout.checkbox.with_payment')} ${termsAndConditionsText}.`
			: `${t('checkout.checkbox.without_payment')} ${termsAndConditionsText}.`;
	}

	const minimumSpendPlainText = t('checkout.checkbox.minimum_spend');
	const acceptedText = requiresMinimumSpendConsent
		? `${minimumSpendPlainText}. ${policyPlainText}`
		: policyPlainText;

	// Header + timer padding
	const headerPadding = 84 + 52;
	const footerPadding = 73 + 12;

	const handleCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIsCheckboxChecked(event.target.checked);
	};

	const handleMinimumSpendCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIsMinimumSpendChecked(event.target.checked);
	};

	// Store the exact policy text for compliance records once every required box is checked,
	// and clear it again if consent is withdrawn so a stale record cannot outlive the consent
	useEffect(() => {
		setAcceptedPolicyText(isCtaEnabled ? acceptedText : '');
	}, [isCtaEnabled, acceptedText]);

	useEffect(() => {
		const handlePopState = () => {
			if (!featureFlags?.enableAddOns) {
				analytics.track('Checkout Session Exited');
				releaseCheckoutSession({ checkoutId: checkoutSession.id });
			}
		};

		window.addEventListener('popstate', handlePopState);
	}, []);

	return (
		<>
			<div
				className={cn('flex flex-1 flex-col bg-tablz-dark-gray-1 overflow-y-scroll', isFirefox ? styles.PolicyScrollFirefox : styles.PolicyScroll)}
				style={
					window.innerWidth <= 640
						? { paddingTop: `${headerPadding}px` }
						: undefined
				}
			>
				{!isLoading
					? tableWithPolicies?.policies?.length > 0
						? <PolicySection table={tableWithPolicies} />
						: requiresRequestFlow(tableWithPolicies)
							? <PolicyItemsLGB />
							: <PolicyItems />
					: null
				}
				<div
					className="mt-auto sm:pb-3 flex flex-col gap-3"
					style={
						window.innerWidth <= 640
							? { paddingBottom: `${footerPadding}px` }
							: undefined
					}
				>
					{requiresMinimumSpendConsent && (
						<AgreementCheckbox
							id='minimum-spend-checkbox'
							data-cy='minimumSpendCheckbox'
							checked={isMinimumSpendChecked}
							label={minimumSpendPlainText}
							onChange={handleMinimumSpendCheckboxClick}
							required={true}
						/>
					)}
					<AgreementCheckbox
						id='agreement-checkbox'
						checked={isCheckboxChecked}
						label={label}
						onChange={handleCheckboxClick}
						required={true}
					/>
				</div>
			</div>

			<div className=" flex flex-col p-2 border-t border-tablz-med-gray-4 bg-tablz-dark-gray-2 fixed bottom-0 left-0 right-0 z-10 sm:sticky">
				<CTAButton
					onClick={() => {
						const nextStep = navConfig.policies?.next || 'details';
						navigate(`../${nextStep}`);
					}}
					disabled={!isCtaEnabled}
					buttonText={getCheckoutCTALabel(CheckoutCTALabel.ACCEPT, t)}
				/>
			</div>
		</>
	);
};
